import { useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';

const TRIAL_DURATION_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

/**
 * Computes trial status from a firstLoginAt timestamp.
 * Pure function — no hooks, safe to call anywhere.
 *
 * @param {string|number|null|undefined} firstLoginAt - ISO string or epoch ms
 * @returns {{ isInFreeTrial: boolean, trialEndsAt: Date|null, daysRemaining: number, hoursRemaining: number }}
 */
export function getTrialStatus(firstLoginAt) {
  if (!firstLoginAt) {
    return { isInFreeTrial: false, trialEndsAt: null, daysRemaining: 0, hoursRemaining: 0 };
  }

  const loginTime = typeof firstLoginAt === 'number' ? firstLoginAt : Date.parse(firstLoginAt);
  if (!loginTime || isNaN(loginTime)) {
    return { isInFreeTrial: false, trialEndsAt: null, daysRemaining: 0, hoursRemaining: 0 };
  }

  const trialEndsAt = new Date(loginTime + TRIAL_DURATION_MS);
  const remaining = trialEndsAt.getTime() - Date.now();

  if (remaining <= 0) {
    return { isInFreeTrial: false, trialEndsAt, daysRemaining: 0, hoursRemaining: 0 };
  }

  return {
    isInFreeTrial: true,
    trialEndsAt,
    daysRemaining: Math.ceil(remaining / (24 * 60 * 60 * 1000)),
    hoursRemaining: Math.ceil(remaining / (60 * 60 * 1000)),
  };
}

/**
 * React hook — returns live trial status derived from the authenticated user's
 * firstLoginAt field.  Re-computes when the user object changes.
 */
export default function useTrialAccess() {
  const { user } = useAuth();
  const firstLoginAt = user?.firstLoginAt || user?.first_login_at || null;

  return useMemo(() => getTrialStatus(firstLoginAt), [firstLoginAt]);
}
